/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable @typescript-eslint/naming-convention */

import { call, all, put, takeLatest, select } from "redux-saga/effects";
import { toast } from "react-toastify";
import semverLte from "semver/functions/lte";
import semverCoerce from "semver/functions/coerce";
import Bridge, { VKBridge } from "@vkontakte/vk-bridge";

import Api from "src/api";
import { AxiosResponse } from "axios";
import {
  IGetRefreshTokenResponse,
} from "src/api/modules/guOAuth";
import { bridgeInvoke, BridgeInvoke, isWebView } from "src/helpers/bridge";
import { setAccessTokenCookie, getSessionCookies } from "src/services/user";

import { APP_INITIALIZATION } from "./const";
import {
  setInit,
  setSession,
  OAuthGosuslugi,
  VkAppVersion,
  AccessToken,
  RefreshToken,
  setVkAppId,
} from "./redux";
import { IVkAppVersion } from "./namespace";
import {
  selectSession,
  selectSettings,
  selectCheckSessionExpiration,
} from "./selectors";
import store from "../../redux/store";
import { userInfoLoadingStarted } from "../../redux/actions/userInfoActions";
import { selectIsDev } from "../../redux/selectors/addresses";
import { HealthCheckEvents } from "../../api/types";

export default function* appServiceSaga(): Generator {
  yield all([
    takeLatest(APP_INITIALIZATION, executeInit, Bridge),
    takeLatest(
      OAuthGosuslugi.pending.toString(),
      executeOAuthGosuslugi,
      bridgeInvoke,
      Api.instance
    ),
    takeLatest(
      VkAppVersion.pending.toString(),
      executeCheckAppVersion,
      bridgeInvoke
    ),
    takeLatest(
      AccessToken.pending.toString(),
      executeGetAccessToken,
      Api.instance
    ),
    takeLatest(
      RefreshToken.pending.toString(),
      executeGetRefreshToken,
      Api.instance
    ),
  ]);
}

function* executeInit(bridge: VKBridge) {
  try {
    const params = new URLSearchParams(window.location.search);
    const vk_app_id = params.get("vk_app_id");
    yield put(setVkAppId(vk_app_id));
    yield call(bridge.subscribe, ({ detail: { type } }) => {
      if (type === "VKWebAppUpdateConfig") {
        bridge.send("VKWebAppSetViewSettings", {
          status_bar_style: "dark",
          action_bar_color: "#fff",
        });
      }
    });
    yield call(bridge.send, "VKWebAppInit");
    yield put(setInit(true));
    yield put(VkAppVersion.pending());
    yield put(setSession(getSessionCookies()));
  } catch (error) {
    yield put(setInit(false));
    yield call(toast, `Не удалось выполнить инициализацию`);
  }
}

interface IVKWebAppOpenExternalLinkReq {
  url: string;
}
interface IVKWebAppOpenExternalLinkRes {
  query_params: string;
}

function* executeOAuthGosuslugi(invoke: BridgeInvoke, api: Api) {
  let externalLinkParams: IVKWebAppOpenExternalLinkReq;

  let errorPayload: {
    eventName?: string;
    payload?: unknown;
  } = {};

  try {
    const { vk_app_id } = yield select(selectSettings);
    const isDev: boolean = yield select(selectIsDev);

    if (isWebView) {
      errorPayload.eventName = "VKWebAppGetClientVersion";
      const { platform, version } = yield call(
        invoke,
        "VKWebAppGetClientVersion"
      );

      const {
        data: { url },
      } = yield call(
        api.guOAuth.getExternalLink,
        vk_app_id,
        platform,
        version,
        isDev || ""
      );

      externalLinkParams = {
        url,
      };

      const externalLinkResult: IVKWebAppOpenExternalLinkRes = yield call(
        invoke,
        "VKWebAppOpenExternalLink",
        externalLinkParams
      );

      errorPayload = {
        eventName: "response IVKWebAppOpenExternalLinkRes",
        payload: externalLinkResult,
      };

      const gosuslugiParams = new URLSearchParams(
        externalLinkResult.query_params
      );
      const code = gosuslugiParams.get("code");

      console.log("setSession({ code }) code: ", code);
      yield put(setSession({ code }));
    }

    yield put(AccessToken.pending());
    yield put(OAuthGosuslugi.fulfilled());
  } catch (error) {
    const data = {
      errorPayload,
      error,
    };

    Api.healthCheckFor(HealthCheckEvents.errorAuthorization).sendFailure(
      JSON.stringify(data)
    );

    console.log(error);
    // eslint-disable-next-line no-console
    console.error("Got error while open external link through VK Bridge");
    // eslint-disable-next-line no-console
    console.error(JSON.stringify(error, null, 2));
    console.log(
      `bridge.send("VKWebAppOpenExternalLink", ${JSON.stringify(
        externalLinkParams,
        null,
        2
      )})`
    );
    yield put(OAuthGosuslugi.rejected(error as Error));
    yield call(
      toast,
      `Не удалось авторизоваться. Попробуйте еще раз или повторите попытку позже`,
      {
        style: {
          marginTop: "40px",
        },
      }
    );
    // yield call(toast, JSON.stringify(error).replaceAll(",", "\n"));
  }
}

function* executeCheckAppVersion(invoke: BridgeInvoke) {
  try {
    const { vk_mobile_app_ver_ios, vk_mobile_app_ver_android } = yield select(
      selectSettings
    );

    const { platform, version }: IVkAppVersion = yield call(
      invoke,
      "VKWebAppGetClientVersion"
    );

    const requiredVersionPlatform =
      platform === "ios" ? vk_mobile_app_ver_ios : vk_mobile_app_ver_android;
    const isValidVersion = semverLte(
      // v1 <= v2
      semverCoerce(requiredVersionPlatform),
      semverCoerce(version)
    );

    if (!isValidVersion) {
      throw Error("Unsupported version");
    }

    yield put(VkAppVersion.fulfilled());
  } catch (error) {
    yield put(VkAppVersion.rejected(error as Error));
    yield call(toast, `Необходимо обновить приложение Вк`);
  }
}

function* executeGetAccessToken(api: Api) {
  try {
    const { vk_app_id } = yield select(selectSettings);
    const { code } = yield select(selectSession);

    yield call(api.guOAuth.getAccessToken, vk_app_id, code);

    yield put(AccessToken.fulfilled());
    store.dispatch(userInfoLoadingStarted());
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Got error for token request (app2app token exchange)");
    // eslint-disable-next-line no-console
    console.log(error);
    yield put(AccessToken.rejected(error as Error));
    yield call(toast, `Не удалось получить код авторизации`);
  }
}

function* executeGetRefreshToken(api: Api) {
  try {
    const { vk_app_id } = yield select(selectSettings);
    const { access_token: stateRefreshToken } = yield select(selectSession);
    const exp: number = yield select(selectCheckSessionExpiration);
    yield call(toast, `exp: ${exp}`);
    yield call(toast, `Cookies: ${JSON.stringify(getSessionCookies())}`);
    const { data }: AxiosResponse<IGetRefreshTokenResponse> = yield call(
      api.guOAuth.getRefreshToken,
      vk_app_id,
      stateRefreshToken
    );
    const { access_token, refresh_token, expires_in } = data;
    yield call(toast, `success: ${JSON.stringify(data)}`);
    yield put(
      setSession({
        access_token,
        refresh_token,
        access_token_expiration_time: expires_in,
        access_token_datetime: new Date().toString(),
      })
    );

    setAccessTokenCookie({
      accessToken: access_token,
      expiresIn: expires_in,
      refreshToken: refresh_token,
      dateTime: new Date().toString(),
      oid: "?oid",
    });

    yield put(RefreshToken.fulfilled());
  } catch (error) {
    yield put(RefreshToken.rejected(error as Error));
    yield call(toast, `Не удалось обновить ключ авторизации`);
    // eslint-disable-next-line no-console
    console.log(error);
  }
}
